import { useEffect, useRef, useState, type FormEvent } from "react";
import { longest, type PersonDto } from "@nabla/api";
import { useActions } from "../../../client/index.ts";
import { designCopy } from "../../../design/copy.ts";
import { formatDeadline, saoPaulo } from "../../../design/format/dates.ts";
import { Button, Buttons } from "../../../design/primitives/Button.tsx";
import { Check, Field, Problem, TextField } from "../../../design/primitives/Field.tsx";
import { Segmented } from "../../../design/primitives/Segmented.tsx";
import { Sheet, SheetForm } from "../../../design/primitives/Sheet.tsx";
import { peopleCopy as t } from "../copy.ts";
import styles from "./People.module.css";

type Role = PersonDto["role"];
type Invited = { readonly shown: true; readonly url: string; readonly until: string } | { readonly shown: false };
type Outcome = Invited | { readonly problem: string } | undefined;
export type Invite = (role: Role, name: string | undefined, clients: readonly string[]) => Promise<Outcome>;
type Client = { readonly id: string; readonly name: string };

export const invited = (answer: { readonly url?: string | undefined } | null, until: string | undefined): Invited =>
  (answer?.url === undefined || until === undefined ? { shown: false } : { shown: true, url: answer.url, until });

export function useInvite(): Invite {
  const actions = useActions();
  return async (role, name) => {
    const outcome = await actions.invite(role, name);
    return outcome.ok ? invited(outcome.value, outcome.value?.invite.expiresAt) : undefined;
  };
}

function Made({ link, access, onClose }: { readonly link: Invited; readonly access?: string | undefined; readonly onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = (url: string) => void navigator.clipboard?.writeText(url).then(() => setCopied(true));
  return (
    <>
      {access === undefined ? null : <p>{access}</p>}
      {link.shown ? (
        <>
          <div className={styles.link}>
            <code className={styles.url}>{link.url}</code>
            <Button onClick={() => copy(link.url)}>{copied ? designCopy.copiado : designCopy.copiar}</Button>
          </div>
          <p>{t.convidar.aviso(formatDeadline(link.until, new Date().toISOString(), saoPaulo))}</p>
        </>
      ) : (
        <p>{t.convidar.jaMostrado}</p>
      )}
      <Buttons>
        <Button variant="primario" onClick={onClose}>
          {designCopy.fechar}
        </Button>
      </Buttons>
    </>
  );
}

function Clients({ clients, chosen, onChange, disabled }: {
  readonly clients: readonly Client[]; readonly chosen: readonly string[]; readonly onChange: (chosen: readonly string[]) => void;
  readonly disabled: boolean;
}) {
  if (clients.length === 0) return <p className={styles.note}>{t.convidar.semClientes}</p>;
  const all = clients.every((client) => chosen.includes(client.id));
  const toggle = (id: string, on: boolean) => onChange(clients.map((c) => c.id).filter((c) => (c === id ? on : chosen.includes(c))));
  return (
    <fieldset className={styles.clients} disabled={disabled}>
      <legend className={styles.legend}>{t.convidar.clientes}</legend>
      <Check checked={all} onChange={(on) => onChange(on ? clients.map((client) => client.id) : [])}>{t.convidar.todos}</Check>
      {clients.map((client) => (
        <Check key={client.id} checked={chosen.includes(client.id)} onChange={(on) => toggle(client.id, on)}>{client.name}</Check>
      ))}
    </fieldset>
  );
}

type InviteProps = { readonly open: boolean; readonly onClose: () => void; readonly invite: Invite; readonly clients?: readonly Client[] };

type Shown = { readonly link: Invited; readonly access: string | undefined };

export function InviteSheet({ open, onClose, invite, clients }: InviteProps) {
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("gestor");
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [made, setMade] = useState<Shown | undefined>(undefined);
  const round = useRef(0);
  useEffect(() => {
    round.current += 1;
    setName("");
    setRole("gestor");
    setChosen([]);
    setSending(false);
    setProblem(undefined);
    setMade(undefined);
  }, [open]);
  const picked = (clients ?? []).filter((client) => chosen.includes(client.id));
  const access = clients === undefined ? undefined : t.convidar.acesso(t.papeis[role], picked.map((client) => client.name));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;
    const typed = name.trim();
    const mine = round.current;
    const sent = access;
    setSending(true);
    setProblem(undefined);
    void invite(role, typed === "" ? undefined : typed, picked.map((client) => client.id)).then((outcome) => {
      if (mine !== round.current) return;
      setSending(false);
      if (outcome !== undefined && "problem" in outcome) setProblem(outcome.problem);
      else if (outcome !== undefined) setMade({ link: outcome, access: sent });
    });
  };
  const roles = (["gestor", "admin"] as const).map((value) => ({ value, label: t.papeis[value] }));
  return (
    <Sheet open={open} title={t.convidar.botao} onClose={onClose}>
      {made ? (
        <Made link={made.link} access={made.access} onClose={onClose} />
      ) : (
        <SheetForm onSubmit={submit}>
          <TextField label={t.convidar.nome} note={t.convidar.opcional} value={name} onChange={setName} maxLength={longest.name} autoComplete="off" disabled={sending} />
          <Field label={t.papel}>
            <Segmented label={t.papel} options={roles} value={role} onChange={setRole} disabled={sending} />
          </Field>
          {clients === undefined ? null : <Clients clients={clients} chosen={chosen} onChange={setChosen} disabled={sending} />}
          {access === undefined ? null : <p className={styles.note} aria-live="polite">{access}</p>}
          {problem === undefined ? null : <Problem>{problem}</Problem>}
          <Buttons>
            <Button variant="primario" type="submit" disabled={sending}>
              {sending ? t.convidar.criando : t.convidar.criar}
            </Button>
          </Buttons>
        </SheetForm>
      )}
    </Sheet>
  );
}
